import * as React from 'react';
import { cn } from '@/lib/utils';
import { Upload, Image as ImageIcon, Loader2, RefreshCw } from 'lucide-react';
import { Button } from './button';

interface FileUploadProps {
  value?: File | null;
  previewUrl?: string | null;
  onChange: (file: File | null) => void;
  onUpload?: (file: File) => Promise<void>;
  accept?: string;
  maxSize?: number; // in bytes
  label?: string;
  description?: string;
  className?: string;
  disabled?: boolean;
  uploading?: boolean;
}

export function FileUpload({
  value,
  previewUrl,
  onChange,
  onUpload,
  accept = 'image/png,image/jpg,image/jpeg,image/gif',
  maxSize = 5 * 1024 * 1024, // 5MB default
  label = 'Telecharger une image',
  description,
  className,
  disabled = false,
  uploading = false,
}: FileUploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [localPreview, setLocalPreview] = React.useState<string | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);

  // Generate local preview when file changes
  React.useEffect(() => {
    if (value) {
      const url = URL.createObjectURL(value);
      setLocalPreview(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setLocalPreview(null);
    }
  }, [value]);

  const displayPreview = localPreview || previewUrl;

  const handleFileSelect = async (file: File) => {
    setError(null);

    // Validate file type
    const acceptedTypes = accept.split(',').map((t) => t.trim());
    if (!acceptedTypes.includes(file.type)) {
      setError('Type de fichier non autorise');
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / 1024 / 1024);
      setError(`Le fichier depasse la taille maximale de ${maxSizeMB}MB`);
      return;
    }

    onChange(file);

    // Auto-upload if onUpload is provided
    if (onUpload) {
      try {
        await onUpload(file);
      } catch (err) {
        setError('Erreur lors du telechargement');
        console.error(err);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemove = () => {
    onChange(null);
    setError(null);
    setLocalPreview(null);
  };

  const handleClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />

      {displayPreview ? (
        // Preview mode - click to replace
        <div
          onClick={handleClick}
          className={cn(
            'group relative cursor-pointer border bg-muted overflow-hidden flex items-center justify-center h-[180px]',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <img
            src={displayPreview}
            alt="Preview"
            className="max-h-[160px] w-auto max-w-full object-contain"
          />
          {uploading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <Loader2 className="h-6 w-6 animate-spin text-white" />
            </div>
          ) : !disabled && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="flex flex-col items-center gap-1 text-white">
                <RefreshCw className="h-5 w-5" />
                <span className="text-xs font-medium">Remplacer</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Upload zone
        <div
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={cn(
            'flex flex-col items-center justify-center gap-2 border-2 border-dashed p-6 transition-colors cursor-pointer',
            isDragging && 'border-primary bg-primary/5',
            !isDragging && 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50',
            disabled && 'cursor-not-allowed opacity-50',
            error && 'border-red-500'
          )}
        >
          {uploading ? (
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          ) : (
            <>
              <div className="flex h-12 w-12 items-center justify-center bg-muted">
                <ImageIcon className="h-6 w-6 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-foreground">{label}</p>
                {description && (
                  <p className="text-xs text-muted-foreground mt-1">{description}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  PNG, JPG, GIF jusqu'a {Math.round(maxSize / 1024 / 1024)}MB
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" disabled={disabled}>
                <Upload className="h-4 w-4 mr-2" />
                Parcourir
              </Button>
            </>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}
